import { set, child, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'

import * as firebaseConnection from '../firebaseConnection'

const ROOT_NODE = 'panel'

type PanelMode = 'manual' | 'auto'

type PanelScreen = number | 'tray'

type PanelState = {
  /** This property tells us if the panel selection has been edited or if it should use the default "auto" mode */
  mode: PanelMode
  /** The ids of the participants selected for panel mode */
  participantIds: readonly number[]
  screen?: PanelScreen
}

const panelState = proxy<PanelState>({
  participantIds: [],
  mode: 'auto',
  screen: undefined,
})

type PanelInitializedState = {
  [key in keyof PanelState]: boolean
}

const panelInitState = proxy<PanelInitializedState>({
  participantIds: false,
  mode: false,
  screen: false,
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  // Adds a Firebase listener that keeps track of the participants in the panel.
  onValue(
    child(child(connection.room, ROOT_NODE), 'participantIds'),
    (snapshot) => {
      panelState.participantIds = snapshot.val() ?? []
      panelInitState.participantIds = true
    }
  )

  // Adds a Firebase listener that triggers when the isPanelControlled state changes.
  onValue(child(child(connection.room, ROOT_NODE), 'mode'), (snapshot) => {
    panelState.mode = snapshot?.val() ?? 'auto'
    panelInitState.mode = true
  })

  // Adds a Firebase listener that triggers when the screen state changes.
  onValue(child(child(connection.room, ROOT_NODE), 'screen'), (snapshot) => {
    panelState.screen = snapshot?.val() ?? undefined
    panelInitState.screen = true
  })
})

// Actions

/**
 * Updates the selected participants and screen in panel mode.
 * @param participantIds - An array of participant IDs to be selected for the panel.
 * @param screen - The screen ID to be used, 'tray' if the tray should be used, or undefined otherwise.
 */
const savePanelState = async (
  participantIds: number[],
  screen: number | 'tray' | undefined
) => {
  const connection = await firebaseConnection.get()
  try {
    await set(child(connection.room, ROOT_NODE), {
      mode: 'manual',
      participantIds,
      screen: screen ?? null,
    })
  } catch (err) {
    connection.logError(
      'SET_PANEL',
      'Failed to set the panel participantIds',
      err
    )
  }
}

const setPanelToAuto = async () => {
  const connection = await firebaseConnection.get()
  try {
    await set(child(connection.room, ROOT_NODE), {
      mode: 'auto',
      participantIds: null,
      screen: null,
    })
  } catch (err) {
    connection.logError('SET_PANEL', 'Failed to set the panel to auto', err)
  }
}

const usePanel = () => useSnapshot(panelState)
const usePanelScreen = () => useSnapshot(panelState).screen
const useIsPanelInitialized = () => {
  const { participantIds, mode, screen } = useSnapshot(panelInitState)
  return participantIds && mode && screen
}

const isPanelInitialized = () =>
  panelInitState.screen && panelInitState.mode && panelInitState.participantIds

export {
  type PanelMode,
  type PanelState,
  type PanelScreen,
  panelState,
  panelInitState,
  usePanel,
  usePanelScreen,
  useIsPanelInitialized,
  isPanelInitialized,
  savePanelState,
  setPanelToAuto,
}
